import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Crown, LayoutDashboard, Users, FileText, TrendingUp,
  CheckSquare, Shield, LogOut, Menu, ChevronRight, Calendar, ShoppingBag, FileSpreadsheet, Zap, Sparkles, Mail, BarChart3, Briefcase, Radio, Headphones, Ticket, Tag, Coins, Globe, CreditCard, Share2, Handshake, Cog, RefreshCw,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { EnvironmentNotice } from './admin/EnvironmentNotice';

const navItems = [
  { href: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
  { href: '/admin/traffic', icon: Radio, label: 'Traffic & Funnel', badge: 'NIEUW' },
  { href: '/admin/crm', icon: BarChart3, label: 'CRM Pipeline', badge: 'NIEUW' },
  { href: '/admin/clients', icon: Users, label: 'Klanten' },
  { href: '/admin/employees', icon: Briefcase, label: 'Medewerkers', badge: 'NIEUW' },
  { href: '/admin/leads', icon: FileSpreadsheet, label: 'Leads (oude)' },
  { href: '/admin/managed', icon: Crown, label: 'Managed Clients' },
  { href: '/admin/ai-queue', icon: Sparkles, label: 'AI Queue' },
  { href: '/admin/email-health', icon: Mail, label: 'E-mail diagnose', badge: 'NIEUW' },
  { href: '/admin/rollenbeheer', icon: Users, label: 'Rollenbeheer', badge: 'NIEUW' },
  { href: '/admin/trials', icon: Ticket, label: 'Trials', badge: 'NIEUW' },
  { href: '/admin/discount-codes', icon: Tag, label: 'Kortingscodes', badge: 'NIEUW' },
  { href: '/admin/commissies', icon: Coins, label: 'Commissies', badge: 'NIEUW' },
  { href: '/admin/website-aanvragen', icon: Globe, label: 'Website-aanvragen', badge: 'NIEUW' },
  { href: '/admin/affiliates', icon: Share2, label: 'Affiliates', badge: 'NIEUW' },
  { href: '/admin/partner-applications', icon: Handshake, label: 'Partner-aanmeldingen', badge: 'NIEUW' },
  { href: '/admin/statistieken', icon: BarChart3, label: 'Statistieken', badge: 'NIEUW' },
  { href: '/admin/audit-log', icon: Shield, label: 'Audit-log', badge: 'NIEUW' },
  { href: '/admin/ops-tickets', icon: Headphones, label: 'Ops Tickets', badge: 'NIEUW' },
  { href: '/admin/templates', icon: Mail, label: 'Email Templates', badge: 'NIEUW' },
  { href: '/admin/invoices', icon: FileText, label: 'Facturen', badge: 'NIEUW' },
  { href: '/admin/reconcile', icon: Shield, label: 'Reconcile', badge: 'NIEUW' },
  { href: '/admin/orders', icon: ShoppingBag, label: 'Bestellingen' },
  { href: '/admin/contracts', icon: FileText, label: 'Contracten' },
  { href: '/admin/revenue', icon: TrendingUp, label: 'Omzet' },
  { href: '/admin/meetings', icon: Calendar, label: 'Meetings' },
  { href: '/admin/tasks', icon: CheckSquare, label: 'Taken' },
  { href: '/admin/test-panel', icon: Zap, label: 'Test Panel' },
  { href: '/admin/settings', icon: Cog, label: 'Instellingen', badge: 'NIEUW' },
  { href: '/admin/email-deliverability', icon: Mail, label: 'Email DNS check', badge: 'NIEUW' },
  { href: '/admin/payment-recoveries', icon: RefreshCw, label: 'Payment Recovery', badge: 'NIEUW' },
  { href: '/admin/audit', icon: Shield, label: 'Audit Log' },
];

export default function AdminLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const Sidebar = () => (
    <aside className="flex flex-col w-64 bg-[#0f172a] border-r border-white/5 min-h-screen">
      <div className="p-6 border-b border-white/5">
        <Link to="/admin" className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#D4A017] rounded-lg flex items-center justify-center">
            <Crown className="w-5 h-5 text-[#0f172a]" />
          </div>
          <div>
            <p className="text-white font-bold text-sm font-serif">IMPERIA AI</p>
            <p className="text-[#D4A017] text-xs">Admin Panel</p>
          </div>
        </Link>
      </div>
      <div className="px-4 py-3 mx-3 my-3 bg-[#D4A017]/10 border border-[#D4A017]/20 rounded-xl">
        <p className="text-[#D4A017] text-xs font-bold uppercase tracking-wider">{user?.role?.toUpperCase()}</p>
        <p className="text-white font-semibold text-sm">{user?.name || 'Admin'}</p>
      </div>
      <nav className="flex-1 px-3 py-2 space-y-1">
        {navItems.map(({ href, icon: Icon, label, badge }) => {
          const active = location.pathname === href || (href !== '/admin' && location.pathname.startsWith(href));
          return (
            <Link
              key={href}
              to={href}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                active ? 'bg-[#D4A017] text-[#0f172a]' : 'text-[#64748b] hover:text-white hover:bg-white/5'
              }`}
            >
              <Icon size={18} />
              {label}
              {badge && !active && (
                <span style={{ marginLeft: 'auto', background: '#D4A017', color: '#0f172a', fontSize: '9px', fontWeight: 800, padding: '1px 6px', borderRadius: '10px' }}>
                  {badge}
                </span>
              )}
              {active && <ChevronRight size={14} className="ml-auto" />}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-white/5 space-y-1">
        <Link to="/platform" className="w-full flex items-center gap-3 px-4 py-2 rounded-xl text-[#64748b] hover:text-white text-sm transition-all">
          <Users size={16} /> Klantportaal
        </Link>
        <button onClick={() => { logout(); navigate('/login'); }} className="w-full flex items-center gap-3 px-4 py-2 rounded-xl text-[#64748b] hover:text-red-400 text-sm transition-all">
          <LogOut size={16} /> Uitloggen
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-[#0a0f1e] flex">
      <div className="hidden lg:flex">
        <Sidebar />
      </div>
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64"><Sidebar /></div>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col max-w-full overflow-x-hidden">
        <header className="h-16 bg-[#0f172a] border-b border-white/5 flex items-center justify-between px-4 md:px-6">
          <button className="lg:hidden text-[#64748b]" onClick={() => setSidebarOpen(true)}><Menu size={22} /></button>
          <div className="flex items-center gap-3 ml-auto">
            <Link to="/" className="text-[#64748b] hover:text-[#D4A017] text-xs transition-colors">← Website</Link>
            <div className="w-8 h-8 bg-[#D4A017] rounded-full flex items-center justify-center text-[#0f172a] font-bold text-sm">
              {(user?.name || 'A')[0].toUpperCase()}
            </div>
          </div>
        </header>
        <EnvironmentNotice />
        <main className="flex-1 p-4 md:p-6 overflow-y-auto overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}
